import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LeaderboardComponent } from './pages/leaderboard/leaderboard.component';
import { HeaderComponent } from './shared/header/header.component';
import { FooterComponent } from './shared/footer/footer.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { ContactComponent } from './pages/contact/contact.component';
import { CariqCloudComponent } from './pages/products/cariq-cloud/cariq-cloud.component';
import { CariqInsuranceCloudComponent } from './pages/products/cariq-insurance-cloud/cariq-insurance-cloud.component';
import { CariqTlsCloudComponent } from './pages/products/cariq-tls-cloud/cariq-tls-cloud.component';
import { CariqScoreComponent } from './pages/products/cariq-score/cariq-score.component';
import { CariqApplicationsComponent } from './pages/products/cariq-applications/cariq-applications.component';
import { AutomotiveSolutionComponent } from './pages/solutions/automotive-solution/automotive-solution.component';
import { TlsSolutionComponent } from './pages/solutions/tls-solution/tls-solution.component';
import { VarrocConnectHardwareComponent } from './pages/products/varroc-connect-hardware/varroc-connect-hardware.component';
import { BfsiComponent } from './pages/solutions/bfsi/bfsi.component';
import { VehicleUsersComponent } from './pages/solutions/vehicle-users/vehicle-users.component';
import { AboutUsComponent } from './pages/policy-pages/about-us/about-us.component';
import { PrivacyPolicyComponent } from './pages/policy-pages/privacy-policy/privacy-policy.component';
import { RefundAndReturnComponent } from './pages/policy-pages/refund-and-return/refund-and-return.component';
import { ShippingComponent } from './pages/policy-pages/shipping/shipping.component';
import { CancellationPolicyComponent } from './pages/policy-pages/cancellation-policy/cancellation-policy.component';
import { TermsAndConditionsComponent } from './pages/policy-pages/terms-and-conditions/terms-and-conditions.component';
import { BusinessLocationComponent } from './pages/business-location/business-location.component';
import { CyberSecurityComponent } from './pages/policy-pages/cyber-security/cyber-security.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { BasicLayoutComponent } from './layout/basic-layout/basic-layout.component';
import { StoreModule } from './store-module/store/store.module';
import { CareerPagesModule } from './pages/career-pages/career-pages.module';
import { ProductsHeaderBannerComponent } from './shared/products-header-banner/products-header-banner.component';
import { SolutionsHeaderBannerComponent } from './shared/solutions-header-banner/solutions-header-banner.component';
import { CareerHeaderBannerComponent } from './shared/career-header-banner/career-header-banner.component';
import { TrackViewDirective } from './helpers/track-view.directive';
import { HeroCanvasDirective } from './helpers/hero-canvas.directive';
import { IlassistPrivacyPolicyComponent } from './pages/ilassist-privacy-policy/ilassist-privacy-policy.component';
import { SeoService } from './service/seo.service';
import { MetaService } from './service/meta.service';
import { SharedModule } from './shared.module';

// New pages
import { PlatformComponent } from './pages/platform/platform.component';
import { ForBusinessComponent } from './pages/solutions/for-business/for-business.component';
import { ForConsumersComponent } from './pages/solutions/for-consumers/for-consumers.component';
import { HardwarePageComponent } from './pages/products/hardware-page/hardware-page.component';
import { ConnectIQComponent } from './pages/products/connectiq/connectiq.component';
import { UnitedSDKComponent } from './pages/products/unitedsdk/unitedsdk.component';
import { ResourcesComponent } from './pages/resources/resources.component';
import { MediaComponent } from './pages/media/media.component';
// ConnectIQ sub-pages
import { BabelfishComponent } from './pages/products/connectiq/babelfish/babelfish.component';
import { W4IQComponent } from './pages/products/connectiq/w4iq/w4iq.component';
import { VCScoreComponent } from './pages/products/connectiq/vc-score/vc-score.component';
import { MilesComponent } from './pages/products/connectiq/miles/miles.component';
import { VStoreComponent } from './pages/products/connectiq/vstore/vstore.component';
import { CAPComponent } from './pages/products/connectiq/cap/cap.component';
// ConnectIQ new microsites
import { OasisComponent } from './pages/products/connectiq/oasis/oasis.component';
import { IIOTComponent } from './pages/products/connectiq/iiot/iiot.component';
import { VehicleAppStoreComponent } from './pages/products/connectiq/vehicle-app-store/vehicle-app-store.component';
import { CertificateManagementSystemComponent } from './pages/products/connectiq/certificate-management-system/certificate-management-system.component';
import { ContextBasedMessagingComponent } from './pages/products/connectiq/context-based-messaging/context-based-messaging.component';
// Solutions For Business sub-pages
import { AutomotiveOEMComponent } from './pages/solutions/for-business/automotive-oem/automotive-oem.component';
import { FleetLogisticsComponent } from './pages/solutions/for-business/fleet-logistics/fleet-logistics.component';
import { InsuranceBFSIComponent } from './pages/solutions/for-business/insurance/insurance.component';
import { EnterpriseComponent } from './pages/solutions/for-business/enterprise/enterprise.component';
// Solutions For Consumers sub-pages
import { ConnectedCarComponent } from './pages/solutions/for-consumers/connected-car/connected-car.component';
import { SafetyAlertsComponent } from './pages/solutions/for-consumers/safety-alerts/safety-alerts.component';
import { ConsumerRewardsComponent } from './pages/solutions/for-consumers/rewards/rewards.component';
import { DeviceStoreComponent } from './pages/solutions/for-consumers/device-store/device-store.component';
// Unified SDK sub-pages
import { MobileSDKComponent } from './pages/products/unitedsdk/mobile-sdk/mobile-sdk.component';
import { WebSDKComponent } from './pages/products/unitedsdk/web-sdk/web-sdk.component';
import { IoTSDKComponent } from './pages/products/unitedsdk/iot-sdk/iot-sdk.component';
// Hardware sub-pages
import { OBDDongleComponent } from './pages/products/hardware-page/obd-dongle/obd-dongle.component';
import { TCUComponent } from './pages/products/hardware-page/tcu/tcu.component';
import { DashCamComponent } from './pages/products/hardware-page/dashcam/dashcam.component';
import { DigitalClusterComponent } from './pages/products/hardware-page/digital-cluster/digital-cluster.component';
import { SolutionsLandingComponent } from './pages/solutions/solutions-landing/solutions-landing.component';
import { ProductsLandingComponent } from './pages/products/products-landing/products-landing.component';
// Admin - Greetings
import { GreetingsDashboardComponent } from './pages/admin/greetings/greetings-dashboard.component';
import { EmailPanelComponent } from './pages/admin/greetings/email-panel.component';
import { NotificationPanelComponent } from './pages/admin/greetings/notification-panel.component';
import { provideShareButtonsOptions } from 'ngx-sharebuttons';
import { shareIcons } from 'ngx-sharebuttons/icons';
import { SchemaService } from './service/schema.service';

@NgModule({
  declarations: [
    AppComponent,
    LeaderboardComponent,
    HeaderComponent,
    FooterComponent,
    DashboardComponent,
    ContactComponent,
    CariqCloudComponent,
    CariqInsuranceCloudComponent,
    CariqTlsCloudComponent,
    CariqScoreComponent,
    CariqApplicationsComponent,
    AutomotiveSolutionComponent,
    TlsSolutionComponent,
    VarrocConnectHardwareComponent,
    BfsiComponent,
    VehicleUsersComponent,
    AboutUsComponent,
    PrivacyPolicyComponent,
    RefundAndReturnComponent,
    ShippingComponent,
    CancellationPolicyComponent,
    TermsAndConditionsComponent,
    BusinessLocationComponent,
    CyberSecurityComponent,
    MainLayoutComponent,
    BasicLayoutComponent,
    ProductsHeaderBannerComponent,
    SolutionsHeaderBannerComponent,
    TrackViewDirective,
    HeroCanvasDirective,
    IlassistPrivacyPolicyComponent,
    // New pages
    PlatformComponent,
    ForBusinessComponent,
    ForConsumersComponent,
    HardwarePageComponent,
    ConnectIQComponent,
    UnitedSDKComponent,
    ResourcesComponent,
    MediaComponent,
    // ConnectIQ sub-pages
    BabelfishComponent,
    W4IQComponent,
    VCScoreComponent,
    MilesComponent,
    VStoreComponent,
    CAPComponent,
    // ConnectIQ new microsites
    OasisComponent,
    IIOTComponent,
    VehicleAppStoreComponent,
    CertificateManagementSystemComponent,
    ContextBasedMessagingComponent,
    // Solutions For Business sub-pages
    AutomotiveOEMComponent,
    FleetLogisticsComponent,
    InsuranceBFSIComponent,
    EnterpriseComponent,
    // Solutions For Consumers sub-pages
    ConnectedCarComponent,
    SafetyAlertsComponent,
    ConsumerRewardsComponent,
    DeviceStoreComponent,
    // Unified SDK sub-pages
    MobileSDKComponent,
    WebSDKComponent,
    IoTSDKComponent,
    // Hardware sub-pages
    OBDDongleComponent,
    TCUComponent,
    DashCamComponent,
    DigitalClusterComponent,
    SolutionsLandingComponent,
    ProductsLandingComponent,
    // Admin - Greetings
    GreetingsDashboardComponent,
    EmailPanelComponent,
    NotificationPanelComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    AppRoutingModule,
    StoreModule,
    CareerPagesModule,
    SharedModule
  ],
  providers: [
    SeoService,
    MetaService,
    SchemaService,
    provideShareButtonsOptions(shareIcons())
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
